import { useEffect, useState } from "react";
import { Image as ImageAntd, Spin } from "antd";
import { LocalStorageKey } from "../../../constants/localStorageKeys";
import ImageDefault from "../../../assets/svg/item-iamge-default.png";

import { fetchImageBlob } from '../../../utils/imageRequest'
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import LoadingGlobalComp from "../../Commons/Loading";

// Module-level caches so a thumbnail is fetched only once per URL — even if the
// card unmounts/remounts (e.g. collapsing & re-opening a Section).
// - blobUrlCache: url -> object URL of the already-downloaded blob
// - inFlightCache: url -> in-flight request, to de-dupe concurrent mounts
const blobUrlCache = new Map<string, string>();
const inFlightCache = new Map<string, Promise<string>>();

const fetchThumbnail = (url: string): Promise<string> =>
{
      const cached = blobUrlCache.get(url);
      if (cached) return Promise.resolve(cached);

      const existing = inFlightCache.get(url);
      if (existing) return existing;

      const request = fetchImageBlob(url)
            .then((res) =>
            {
                  if (res.status !== 200) throw new Error("Thumbnail request failed");
                  const objectUrl = URL.createObjectURL(res.data);
                  blobUrlCache.set(url, objectUrl);
                  return objectUrl;
            })
            .finally(() =>
            {
                  // Allow a retry later if the request failed.
                  inFlightCache.delete(url);
            });

      inFlightCache.set(url, request);
      return request;
};

const Thumbnail = ({ url, token }: { url: any; token: string }) =>
{
      // "loading" -> spinner, "done" -> real image, "error" -> default image.
      const [imgSrc, setImgSrc] = useState<string>();
      const [status, setStatus] = useState<"loading" | "done" | "error">(
            () => (url && blobUrlCache.get(url) ? "done" : "loading")
      );

      const staticConfig = localStorage.getItem(LocalStorageKey.STATIC_CONFIG) ?? '';

      const parsedConfig = JSON.parse(staticConfig);
      const authMethod = parsedConfig.REACT_APP_AUTHENTICATION_METHOD;

      useEffect(() =>
      {
            if (!url)
            {
                  setImgSrc(ImageDefault);
                  setStatus("error");
                  return;
            }

            if (authMethod === "COOKIES")
            {
                  // The browser loads the image itself; onLoad/onError below settle the status.
                  setImgSrc(url);
                  setStatus("loading");
                  return;
            }

            // Cache hit -> reuse the already-downloaded blob, no new API call.
            const cached = blobUrlCache.get(url);
            if (cached)
            {
                  setImgSrc(cached);
                  setStatus("done");
                  return;
            }

            let active = true;
            setImgSrc(undefined);
            setStatus("loading");

            fetchThumbnail(url)
                  .then((objectUrl) =>
                  {
                        if (!active) return;
                        setImgSrc(objectUrl);
                        setStatus("done");
                  })
                  .catch(() =>
                  {
                        if (!active) return;
                        setImgSrc(ImageDefault);
                        setStatus("error");
                  });

            return () =>
            {
                  active = false;
            };

      }, [url, token, authMethod]);

      const isCookies = authMethod === "COOKIES";
      const showSpinner = status === "loading";

      return (
            <>
                  {imgSrc && (
                        <ImageAntd
                              src={imgSrc}
                              alt="thumbnail"
                              className="h-full"
                              style={showSpinner ? { visibility: "hidden" } : undefined}
                              preview={false}
                              fallback={ImageDefault}
                              onLoad={() =>
                              {
                                    if (isCookies) setStatus((prev) => (prev === "error" ? prev : "done"));
                              }}
                              onError={() =>
                              {
                                    if (isCookies)
                                    {
                                          setImgSrc(ImageDefault);
                                          setStatus("error");
                                    }
                              }}
                        />
                  )}
                  {showSpinner && (
                        <div
                              style={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                              }}
                        >
                              <LoadingGlobalComp size={28} />
                        </div>
                  )}
            </>
      );
};

export default Thumbnail
